// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import { KeyValue } from "./keyValue";
import { ITranslation } from "./translation";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StaticImplements<I, _C extends I> = any;
/**
 * Global shared Interfaces for all models
 */

/**
 * action performed on an entity
 */
export enum ACTION_STATUS_ENTITY {
  ACTIVE = 0,
  CREATED = 1,
  MODIFIED = 2,
  DELETED = 3,
}

/**
 * status of an entity
 */
export enum STATUS_ENTITY {
  ACTIVE = 1,
  ARCHIVED = 2,
  SHARED = 3,
  LOCKED = 4,
  PASSWORDRESET = 5, // for User
  CANCELLED = 6, // for Appointment: CANCELLED = cancelled by 3rd party (not same as ACTION_STATUS_ENTITY.DELETED), user has to confirm before deletion
  INVITED = 7, // for RelatedPerson
  DELETED = 8,
}
export enum AccessLevel {
  NONE = 0,
  READ = 1,
  WRITE = 10, // write acces automatically gives read access
  // No "publish" level. If you gives the "write" access and they have the "publish" permission, they will be able to publish
  // No "delete" level either. Write access and "delete" permission will give the right to delete
}

/**
 * Based Entity Object
 */
export class IEntity {
  public static get RISING(): string {
    return "rising";
  }
  public static get MORNING(): string {
    return "morning";
  }
  public static get NOON(): string {
    return "noon";
  }
  public static get EVENING(): string {
    return "evening";
  }
  public static get BEDING(): string {
    return "beding";
  }

  public static get TIMING_RISING(): string {
    return "R";
  }
  public static get TIMING_MORNING(): string {
    return "M";
  }
  public static get TIMING_NOON(): string {
    return "N";
  }
  public static get TIMING_EVENING(): string {
    return "E";
  }
  public static get TIMING_BED(): string {
    return "B";
  }
  public static get TIMING_NONE(): string {
    return "";
  }

  public static get LS_TIMING() {
    return [IEntity.TIMING_RISING, IEntity.TIMING_MORNING, IEntity.TIMING_NOON, IEntity.TIMING_EVENING, IEntity.TIMING_BED];
  }

  public _id: string;
  public creation?: string;
  public modified: string;
  public actionStatus?: ACTION_STATUS_ENTITY;
  public entityStatus: STATUS_ENTITY[]; // an array because an entity can be ARCHIVED and SHARED

  /**
   *
   * @param value a string representing a timing IEntity.RISING, IEntity.MORNING, etc
   * @returns the corresponding timing code IEntity.TIMING_RISING, IEntity.TIMING_MORNING etc
   */
  public static toTimingCode(value: string) {
    switch (value) {
      case IEntity.RISING:
        return IEntity.TIMING_RISING;
      case IEntity.MORNING:
        return IEntity.TIMING_MORNING;
      case IEntity.NOON:
        return IEntity.TIMING_NOON;
      case IEntity.EVENING:
        return IEntity.TIMING_EVENING;
      case IEntity.BEDING:
        return IEntity.TIMING_BED;
      default:
        return null;
    }
  }

  /**
   *
   * @param value a string representing a timing code IEntity.TIMING_RISING, IEntity.TIMING_MORNING etc
   * @returns the corresponding timing IEntity.RISING, IEntity.MORNING, etc
   */
  public static fromTimingCode(value: string) {
    switch (value) {
      case IEntity.TIMING_RISING:
        return IEntity.RISING;
      case IEntity.TIMING_MORNING:
        return IEntity.MORNING;
      case IEntity.TIMING_NOON:
        return IEntity.NOON;
      case IEntity.TIMING_EVENING:
        return IEntity.EVENING;
      case IEntity.TIMING_BED:
        return IEntity.BEDING;
      default:
        return null;
    }
  }

  /**
   * Is it locked ?
   * @param entity
   */
  public static isLocked(entity: IEntity): boolean {
    if (!entity.entityStatus || entity.entityStatus.length === 0) return false;
    return entity.entityStatus.indexOf(STATUS_ENTITY.LOCKED) >= 0;
  }

  /**
   * Set this entity locked
   * @param entity
   */
  public static setLocked(entity: IEntity) {
    if (!entity.entityStatus || entity.entityStatus.length === 0) entity.entityStatus = [];
    if (!IEntity.isLocked(entity)) entity.entityStatus.push(STATUS_ENTITY.LOCKED);
  }

  /**
   * Is it archived ?
   * @param entity
   */
  public static isArchived(entity: IEntity): boolean {
    if (!entity.entityStatus || entity.entityStatus.length === 0) return false;
    return entity.entityStatus.indexOf(STATUS_ENTITY.ARCHIVED) >= 0;
  }

  /**
   * Archived a entity (also remove ACTIVE status)
   * @param entity
   */
  public static setArchived(entity: IEntity) {
    if (!entity.entityStatus || entity.entityStatus.length === 0) entity.entityStatus = [];
    // remove ACTIVE if present and set ARCHIVED
    const idx = entity.entityStatus.indexOf(STATUS_ENTITY.ACTIVE);
    if (idx >= 0) entity.entityStatus[idx] = STATUS_ENTITY.ARCHIVED;
    else entity.entityStatus.push(STATUS_ENTITY.ARCHIVED);
  }

  /**
   * Is it cancelled (appointment) ?
   * @param entity
   */
  public static isCancelled(entity: IEntity): boolean {
    if (!entity.entityStatus || entity.entityStatus.length === 0) return false;
    return entity.entityStatus.indexOf(STATUS_ENTITY.CANCELLED) >= 0;
  }

  /**
   * Is it shared/Public ?
   * @param entity
   */
  public static isPublicShared(entity: IEntity): boolean {
    if (!entity.entityStatus || entity.entityStatus.length === 0) return false;
    return entity.entityStatus.indexOf(STATUS_ENTITY.SHARED) >= 0;
  }

  /**
   * Set this entity shared
   * @param entity
   */
  public static setShared(entity: IEntity) {
    if (!entity.entityStatus || entity.entityStatus.length === 0) entity.entityStatus = [];
    if (!IEntity.isPublicShared(entity)) entity.entityStatus.push(STATUS_ENTITY.SHARED);
  }

  /**
   * Set this entity private (not shared)
   * @param entity
   */
  public static setPrivate(entity: IEntity) {
    if (!entity.entityStatus || entity.entityStatus.length === 0) entity.entityStatus = [];
    if (IEntity.isPublicShared(entity)) {
      const idx = entity.entityStatus.indexOf(STATUS_ENTITY.SHARED);
      entity.entityStatus.splice(idx, 1);
    }
  }

  /**
   * Transforme TimingCode into 3 KeyValues
   * Ex, for "ME" code: KeyValue["morning", true], KeyValue["noon", false], KeyValue["evening", true]
   * @param code
   */
  public static toKeyValues(code: string): KeyValue[] {
    const timings = new Array<KeyValue>();
    if (!code) code = ""; // always return 3 KeyValues
    timings.push(new KeyValue(IEntity.RISING, code.indexOf(IEntity.TIMING_RISING) >= 0));
    timings.push(new KeyValue(IEntity.MORNING, code.indexOf(IEntity.TIMING_MORNING) >= 0));
    timings.push(new KeyValue(IEntity.NOON, code.indexOf(IEntity.TIMING_NOON) >= 0));
    timings.push(new KeyValue(IEntity.EVENING, code.indexOf(IEntity.TIMING_EVENING) >= 0));
    timings.push(new KeyValue(IEntity.BEDING, code.indexOf(IEntity.TIMING_BED) >= 0));
    return timings;
  }

  public static isDeleted(entity: IEntity): boolean {
    return entity?.actionStatus === ACTION_STATUS_ENTITY.DELETED || entity?.entityStatus?.includes(STATUS_ENTITY.DELETED);
  }

  /** Archived a entity (also remove ACTIVE status)
   * @param entity
   */
  public static setDeleted(entity: IEntity) {
    if (!entity.entityStatus) {
      entity.entityStatus = [];
    } else {
      // clean all deleted status already in array, if none exist array stay the same
      entity.entityStatus = entity.entityStatus.filter((v) => v !== STATUS_ENTITY.DELETED);
    }

    // remove ACTIVE if present and set DELETED
    const idxA = entity.entityStatus.indexOf(STATUS_ENTITY.ACTIVE);

    if (idxA >= 0) {
      entity.entityStatus[idxA] = STATUS_ENTITY.DELETED;
    } else {
      entity.entityStatus.push(STATUS_ENTITY.DELETED);
    }
  }
}

export interface Identifier {
  use?: string;
  system: string;
  value: string;
  label?: string;
}

export interface Coding {
  display?: string;
  system: string;
  code: string;
  checked?: boolean; // this is only used in frontend to help questions with multiple choices
}

export interface Codes {
  coding?: Coding[];
  text?: string;
}

export interface Address {
  use: string;
  line: string[];
  postalCode: string;
  city: string;
  country: string;
  text?: string;
}

export interface Location {
  address: Address;
  telecom: Identifier;
}

export interface Name {
  use: string;
  text?: string;
  family: string[];
  given: string[];
  prefix?: string[];
}

export enum RULE_TARGET_TYPE {
  DRUGS = "drugs",
  VITAL_PROFILE = "vitalProfile",
}

export interface Reference {
  reference: string;
  display: string;
  type?: string;
}

export interface Quantity {
  code?: string;
  value?: number;
  valueArray?: number[][]; // homemade fields to manage Glucose
  system?: string;
  unit?: string;
}

export interface Period {
  start: string;
  end?: string; // if no end, Period ontgoing indefinitely
}

export interface Quantities {
  rise: string;
  morning: string;
  noon: string;
  evening: string;
  bedtime: string;
}

export interface Timing {
  boundsPeriod?: Period;
  count?: number;
  frequency?: number;
  period?: number;
  periodUnits?: string;
  timeOfDay?: string[]; // times during the day, in the format hh:mm:ss
  // extra params (not in FHIR)
  when?: string; // on "week" "when", define days of week "MON,TUE,WED,THU,FRI,SAT,SUN". Ex: "MON;FRI;SUN"
  // on "month" "when" define days "1,2,3,...31". Ex: "1;15;30"
  // on "year" "when" define exact dates (ISO 8601 Datetimes).
  timingCode?: string; // R=rise, M=morning, N=noon, E=evening, B=bedtime  (1, 2 or 3 letters, no separator)
  endless?: boolean; // no end period
  quantities?: Quantities;
  skipFirst?: boolean; // during a cyclic event, indicates whether to generate something from the beginning or not
  asNecessary?: boolean;
  event?: string[]; // When the event occurs, list of dates
  startAtEnd?: boolean;
}

export interface ScheduledTiming {
  repeat: Timing;
  event?: string[];
  code?: Codes;
}

export interface Range {
  low?: string;
  high?: string;
}

export interface Notation {
  authorReference?: Reference;
  time?: string;
  text: string;
}

/**
 * enum for activity locations
 */
export enum ACTIVITY_LOCATION {
  HOME,
  HOSPITAL,
  OTHER,
}

/**
 * enum for activity categories
 */
export enum ACTIVITY_CATEGORY {
  PROCEDURE,
  DRUG,
  OBSERVATION,
  SUPPLY,
  DIET,
  ENCOUNTER,
  REVALIDATION,
  OTHER,
}

/**
 * enum for scheduled period
 */
export enum SCHEDULE_PERIOD {
  MINUTE,
  HOUR,
  DAY,
  WEEK,
  MONTH,
  YEAR,
}

export interface ITranslationKv {
  lang: string;
  values: [
    {
      key: string;
      value: string;
    }
  ];
}

export interface IVitalProfileDefinition {
  code: string; // ce qui permet d'identifier la valeur de référence (code loinc ou snomed)
  unit?: ITranslation; // unit de la valeur de référence
  type: string; // number, table? pour la glycémie
  min?: number; // valeur minimum pour la valeur
  max?: number; // valeur maximum
  readOnlyMobile: boolean; // indique si la patient peut modifier la valeur
  nameMobile: ITranslation; // nom affiché sur le mobile
  nameDashboard: ITranslation; // nom affiché sur le dashboard
  priority: number; // permet de gérer l'ordre d'affichage
  columns: IVitalProfileColumn[];
  rows: IVitalProfileRow[];
  hiddenForMobile?: boolean;
}

export interface IVitalProfileColumn {
  coding?: Coding;
  name?: ITranslation;
}
export interface IVitalProfileRow {
  coding?: Coding;
  name?: ITranslation;
  start?: number;
  end?: number;
  unit?: ITranslation;
}

/**
 * Quiz specific use case (ex: quiz, consent)
 */
export enum SPECIFIC_USE {
  NONE = 0,
  QUIZ = 1,
  CONSENT = 2,
}

export interface InfoOrganization {
  telecom: Identifier;
  address: Address;
}
